import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
    DollarSign, 
    Car, 
    Calendar, 
    TrendingUp, 
    TrendingDown,
    Plus,
    Minus,
    FileText,
    Download,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Receipt,
    Clock,
    Building2,
    Save,
    User,
    MapPin,
    Phone,
    Mail,
    Hash
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';

export default function BillingPage() {
    const { t, language } = useLanguage();
    const [loading, setLoading] = useState(true);
    const [billingSummary, setBillingSummary] = useState(null);
    const [billingHistory, setBillingHistory] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    
    // Billing Details State
    const [billingDetails, setBillingDetails] = useState({
        company_name: '',
        vat_number: '',
        tax_office: '',
        profession: '',
        address: '',
        city: '',
        postal_code: '',
        country: 'Ελλάδα',
        phone: '',
        email: ''
    });
    const [savingDetails, setSavingDetails] = useState(false);
    const [detailsLoaded, setDetailsLoaded] = useState(false);

    const months = language === 'el' 
        ? ['Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος', 
           'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος']
        : ['January', 'February', 'March', 'April', 'May', 'June',
           'July', 'August', 'September', 'October', 'November', 'December'];

    useEffect(() => {
        fetchBillingData();
        fetchBillingDetails();
    }, [selectedYear, selectedMonth]);

    const fetchBillingDetails = async () => {
        try {
            const response = await api.get('/billing/details');
            if (response.data) {
                setBillingDetails(response.data);
            }
            setDetailsLoaded(true);
        } catch (error) {
            console.error('Error fetching billing details:', error);
            setDetailsLoaded(true);
        }
    };

    const saveBillingDetails = async () => {
        setSavingDetails(true);
        try {
            await api.put('/billing/details', billingDetails);
            toast.success(language === 'el' ? 'Τα στοιχεία αποθηκεύτηκαν!' : 'Details saved successfully!');
        } catch (error) {
            console.error('Error saving billing details:', error);
            toast.error(language === 'el' ? 'Σφάλμα αποθήκευσης' : 'Error saving details');
        } finally {
            setSavingDetails(false);
        }
    };

    const handleDetailChange = (field, value) => {
        setBillingDetails(prev => ({ ...prev, [field]: value }));
    };

    const fetchBillingData = async () => {
        setLoading(true);
        try {
            const [summaryRes, historyRes] = await Promise.all([
                api.get(`/billing/summary?year=${selectedYear}&month=${selectedMonth}`),
                api.get('/billing/history?months=12')
            ]);
            setBillingSummary(summaryRes.data);
            setBillingHistory(historyRes.data);
        } catch (error) {
            if (error.response?.status === 403) {
                window.location.replace('/app/dashboard');
                return;
            }
            console.error('Error fetching billing data:', error);
            toast.error(language === 'el' ? 'Σφάλμα φόρτωσης δεδομένων χρέωσης' : 'Error loading billing data');
        } finally {
            setLoading(false);
        }
    };

    const navigateMonth = (direction) => {
        let newMonth = selectedMonth + direction;
        let newYear = selectedYear;
        
        if (newMonth > 12) {
            newMonth = 1;
            newYear++;
        } else if (newMonth < 1) {
            newMonth = 12;
            newYear--;
        }
        
        setSelectedMonth(newMonth);
        setSelectedYear(newYear);
    };

    const exportToCSV = () => {
        if (!billingSummary) return;
        
        const headers = ['Vehicle', 'Status', 'Days Active', 'Daily Rate (€)', 'Charge (€)', 'Added Date', 'Removed Date'];
        const rows = billingSummary.usage_details.map(u => [
            u.vehicle_info,
            u.status,
            u.days_active,
            u.daily_rate.toFixed(4),
            u.charge.toFixed(2),
            u.added_date ? new Date(u.added_date).toLocaleDateString() : '-',
            u.removed_date ? new Date(u.removed_date).toLocaleDateString() : '-'
        ]);
        
        // Add summary row
        rows.push([]);
        rows.push(['TOTAL', '', '', '', billingSummary.net_amount.toFixed(2), '', '']);
        
        const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `billing_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast.success(language === 'el' ? 'Εξαγωγή CSV ολοκληρώθηκε' : 'CSV exported successfully');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <Badge className="bg-green-100 text-green-800">{language === 'el' ? 'Ενεργό' : 'Active'}</Badge>;
            case 'added_mid_month':
                return <Badge className="bg-blue-100 text-blue-800">{language === 'el' ? 'Προστέθηκε' : 'Added'}</Badge>;
            case 'removed_mid_month':
                return <Badge className="bg-orange-100 text-orange-800">{language === 'el' ? 'Αφαιρέθηκε' : 'Removed'}</Badge>;
            default:
                return <Badge variant="secondary">{status}</Badge>;
        }
    };

    const formatCurrency = (amount) => `€${(amount || 0).toFixed(2)}`;

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-[#1B7EB5]" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6" data-testid="billing-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">
                        {language === 'el' ? 'Χρεώσεις & Τιμολόγηση' : 'Billing & Invoicing'}
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {language === 'el' 
                            ? 'Παρακολουθήστε τη χρήση οχημάτων και τις χρεώσεις σας' 
                            : 'Track your vehicle usage and charges'}
                    </p>
                </div>
                <Button 
                    onClick={exportToCSV}
                    variant="outline"
                    className="border-[#1B7EB5] text-[#1B7EB5]"
                    data-testid="export-billing-csv"
                >
                    <Download className="w-4 h-4 mr-2" />
                    {language === 'el' ? 'Εξαγωγή CSV' : 'Export CSV'}
                </Button>
            </div>

            {/* Month Selector */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                        <Button 
                            variant="ghost" 
                            onClick={() => navigateMonth(-1)}
                            data-testid="prev-month"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <div className="text-center">
                            <h2 className="text-xl font-semibold text-slate-800">
                                {months[selectedMonth - 1]} {selectedYear}
                            </h2>
                            {billingSummary && (
                                <p className="text-sm text-slate-500">
                                    {language === 'el' ? 'Ημέρα' : 'Day'} {billingSummary.billing_period.current_day} / {billingSummary.billing_period.days_in_period}
                                </p>
                            )}
                        </div>
                        <Button 
                            variant="ghost" 
                            onClick={() => navigateMonth(1)}
                            disabled={selectedYear === new Date().getFullYear() && selectedMonth === new Date().getMonth() + 1}
                            data-testid="next-month"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Summary Cards */}
            {billingSummary && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-[#1B7EB5]/10 rounded-lg">
                                    <Car className="w-5 h-5 text-[#1B7EB5]" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">
                                        {language === 'el' ? 'Τρέχοντα Οχήματα' : 'Current Vehicles'}
                                    </p>
                                    <p className="text-2xl font-bold text-slate-800">
                                        {billingSummary.current_vehicle_count}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <DollarSign className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">
                                        {language === 'el' ? 'Τιμή/Όχημα' : 'Price/Vehicle'}
                                    </p>
                                    <p className="text-2xl font-bold text-slate-800">
                                        €{billingSummary.price_per_vehicle}
                                        <span className="text-sm font-normal text-slate-500">/{language === 'el' ? 'μήνα' : 'mo'}</span>
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                    <Receipt className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">
                                        {language === 'el' ? 'Συνολικές Χρεώσεις' : 'Total Charges'}
                                    </p>
                                    <p className="text-2xl font-bold text-slate-800">
                                        {formatCurrency(billingSummary.total_charges)}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-2 border-[#1B7EB5]">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-[#1B7EB5] rounded-lg">
                                    <FileText className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">
                                        {language === 'el' ? 'Καθαρό Ποσό' : 'Net Amount'}
                                    </p>
                                    <p className="text-2xl font-bold text-[#1B7EB5]">
                                        {formatCurrency(billingSummary.net_amount)}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Vehicle Usage Details */}
            {billingSummary && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Car className="w-5 h-5" />
                            {language === 'el' ? 'Αναλυτική Χρήση Οχημάτων' : 'Vehicle Usage Details'}
                        </CardTitle>
                        <CardDescription>
                            {language === 'el' 
                                ? `Ημερήσια χρέωση: €${(billingSummary.price_per_vehicle / (billingSummary.billing_period?.days_in_period || 30)).toFixed(4)}/όχημα` 
                                : `Daily rate: €${(billingSummary.price_per_vehicle / (billingSummary.billing_period?.days_in_period || 30)).toFixed(4)}/vehicle`}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-slate-200">
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">
                                            {language === 'el' ? 'Όχημα' : 'Vehicle'}
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">
                                            {language === 'el' ? 'Κατάσταση' : 'Status'}
                                        </th>
                                        <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">
                                            {language === 'el' ? 'Ημέρες' : 'Days'}
                                        </th>
                                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">
                                            {language === 'el' ? 'Χρέωση' : 'Charge'}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {billingSummary.usage_details.map((usage, index) => (
                                        <tr 
                                            key={usage.vehicle_id} 
                                            className={`border-b border-slate-100 hover:bg-slate-50 ${
                                                usage.status === 'removed_mid_month' ? 'opacity-60' : ''
                                            }`}
                                        >
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2">
                                                    {usage.status === 'added_mid_month' && (
                                                        <Plus className="w-4 h-4 text-blue-500" />
                                                    )}
                                                    {usage.status === 'removed_mid_month' && (
                                                        <Minus className="w-4 h-4 text-orange-500" />
                                                    )}
                                                    <span className="font-medium text-slate-800">
                                                        {usage.vehicle_info}
                                                    </span>
                                                </div>
                                                {(usage.added_date && usage.status !== 'active') && (
                                                    <p className="text-xs text-slate-500 mt-1">
                                                        {language === 'el' ? 'Προστέθηκε' : 'Added'}: {new Date(usage.added_date).toLocaleDateString()}
                                                        {usage.removed_date && (
                                                            <> • {language === 'el' ? 'Αφαιρέθηκε' : 'Removed'}: {new Date(usage.removed_date).toLocaleDateString()}</>
                                                        )}
                                                    </p>
                                                )}
                                            </td>
                                            <td className="py-3 px-4">
                                                {getStatusBadge(usage.status)}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span className="font-medium">{usage.days_active}</span>
                                                <span className="text-slate-500 text-sm"> / {billingSummary.billing_period.days_in_period}</span>
                                            </td>
                                            <td className="py-3 px-4 text-right font-medium text-slate-800">
                                                {formatCurrency(usage.charge)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-slate-50">
                                        <td colSpan="3" className="py-3 px-4 text-right font-semibold text-slate-800">
                                            {language === 'el' ? 'Σύνολο Χρεώσεων:' : 'Total Charges:'}
                                        </td>
                                        <td className="py-3 px-4 text-right font-bold text-lg text-slate-800">
                                            {formatCurrency(billingSummary.total_charges)}
                                        </td>
                                    </tr>
                                    {billingSummary.total_credits > 0 && (
                                        <tr className="bg-green-50">
                                            <td colSpan="3" className="py-3 px-4 text-right font-semibold text-green-700">
                                                {language === 'el' ? 'Πιστώσεις (αφαιρέσεις):' : 'Credits (removals):'}
                                            </td>
                                            <td className="py-3 px-4 text-right font-bold text-green-700">
                                                -{formatCurrency(billingSummary.total_credits)}
                                            </td>
                                        </tr>
                                    )}
                                    <tr className="bg-[#1B7EB5]/10">
                                        <td colSpan="3" className="py-3 px-4 text-right font-bold text-[#1B7EB5]">
                                            {language === 'el' ? 'Καθαρό Ποσό:' : 'Net Amount:'}
                                        </td>
                                        <td className="py-3 px-4 text-right font-bold text-xl text-[#1B7EB5]">
                                            {formatCurrency(billingSummary.net_amount)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Recent Activity Log */}
            {billingSummary && billingSummary.usage_logs.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            {language === 'el' ? 'Πρόσφατες Αλλαγές' : 'Recent Changes'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {billingSummary.usage_logs.slice(0, 10).map((log, index) => (
                                <div 
                                    key={log.id}
                                    className="flex items-center gap-3 p-3 rounded-lg bg-slate-50"
                                >
                                    <div className={`p-2 rounded-full ${
                                        log.action === 'added' ? 'bg-blue-100' : 'bg-orange-100'
                                    }`}>
                                        {log.action === 'added' 
                                            ? <Plus className="w-4 h-4 text-blue-600" />
                                            : <Minus className="w-4 h-4 text-orange-600" />
                                        }
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium text-slate-800">
                                            {log.vehicle_info}
                                        </p>
                                        <p className="text-sm text-slate-500">
                                            {log.action === 'added' 
                                                ? (language === 'el' ? 'Προστέθηκε' : 'Added')
                                                : (language === 'el' ? 'Αφαιρέθηκε' : 'Removed')
                                            }
                                            {' • '}
                                            {new Date(log.timestamp).toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Plan Info */}
            {billingSummary && (
                <Card className="bg-gradient-to-r from-[#1B7EB5]/5 to-[#8CC774]/5">
                    <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div>
                                <p className="text-sm text-slate-500 mb-1">
                                    {language === 'el' ? 'Τρέχον Πλάνο' : 'Current Plan'}
                                </p>
                                <h3 className="text-xl font-bold text-slate-800 capitalize">
                                    {billingSummary.plan}
                                </h3>
                                <p className="text-slate-600">
                                    €{billingSummary.price_per_vehicle}/{language === 'el' ? 'όχημα/μήνα' : 'vehicle/month'}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-slate-500 mb-1">
                                    {language === 'el' ? 'Εταιρεία' : 'Company'}
                                </p>
                                <p className="font-semibold text-slate-800">
                                    {billingSummary.company_name}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Billing Details Card */}
            <Card data-testid="billing-details-card">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-[#1B7EB5]" />
                        {language === 'el' ? 'Στοιχεία Τιμολόγησης' : 'Billing Details'}
                    </CardTitle>
                    <CardDescription>
                        {language === 'el' 
                            ? 'Συμπληρώστε τα στοιχεία σας για την έκδοση τιμολογίων' 
                            : 'Fill in your details for invoice generation'}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Company Name */}
                        <div className="space-y-2">
                            <Label htmlFor="company_name" className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-slate-500" />
                                {language === 'el' ? 'Επωνυμία Επιχείρησης' : 'Company Name'}
                            </Label>
                            <Input
                                id="company_name"
                                value={billingDetails.company_name}
                                onChange={(e) => handleDetailChange('company_name', e.target.value)}
                                placeholder={language === 'el' ? 'π.χ. Εταιρεία ΑΕ' : 'e.g. Company Ltd'}
                                data-testid="billing-company-name"
                            />
                        </div>

                        {/* VAT Number */}
                        <div className="space-y-2">
                            <Label htmlFor="vat_number" className="flex items-center gap-2">
                                <Hash className="w-4 h-4 text-slate-500" />
                                {language === 'el' ? 'ΑΦΜ' : 'VAT Number'}
                            </Label>
                            <Input
                                id="vat_number"
                                value={billingDetails.vat_number}
                                onChange={(e) => handleDetailChange('vat_number', e.target.value)}
                                placeholder={language === 'el' ? 'π.χ. 123456789' : 'e.g. 123456789'}
                                data-testid="billing-vat-number"
                            />
                        </div>

                        {/* Tax Office */}
                        <div className="space-y-2">
                            <Label htmlFor="tax_office" className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-slate-500" />
                                {language === 'el' ? 'ΔΟΥ' : 'Tax Office'}
                            </Label>
                            <Input
                                id="tax_office"
                                value={billingDetails.tax_office}
                                onChange={(e) => handleDetailChange('tax_office', e.target.value)}
                                placeholder={language === 'el' ? 'π.χ. Α\' Αθηνών' : 'e.g. Athens A'}
                                data-testid="billing-tax-office"
                            />
                        </div>

                        {/* Profession */}
                        <div className="space-y-2">
                            <Label htmlFor="profession" className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-500" />
                                {language === 'el' ? 'Επάγγελμα/Δραστηριότητα' : 'Profession/Activity'}
                            </Label>
                            <Input
                                id="profession"
                                value={billingDetails.profession}
                                onChange={(e) => handleDetailChange('profession', e.target.value)}
                                placeholder={language === 'el' ? 'π.χ. Μεταφορές' : 'e.g. Transportation'}
                                data-testid="billing-profession"
                            />
                        </div>

                        {/* Address */}
                        <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="address" className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-slate-500" />
                                {language === 'el' ? 'Διεύθυνση' : 'Address'}
                            </Label>
                            <Input
                                id="address"
                                value={billingDetails.address}
                                onChange={(e) => handleDetailChange('address', e.target.value)}
                                placeholder={language === 'el' ? 'π.χ. Λεωφόρος Αθηνών 100' : 'e.g. 100 Athens Avenue'}
                                data-testid="billing-address"
                            />
                        </div>

                        {/* City */}
                        <div className="space-y-2">
                            <Label htmlFor="city">
                                {language === 'el' ? 'Πόλη' : 'City'}
                            </Label>
                            <Input
                                id="city"
                                value={billingDetails.city}
                                onChange={(e) => handleDetailChange('city', e.target.value)}
                                placeholder={language === 'el' ? 'π.χ. Αθήνα' : 'e.g. Athens'}
                                data-testid="billing-city"
                            />
                        </div>

                        {/* Postal Code */}
                        <div className="space-y-2">
                            <Label htmlFor="postal_code">
                                {language === 'el' ? 'Τ.Κ.' : 'Postal Code'}
                            </Label>
                            <Input
                                id="postal_code"
                                value={billingDetails.postal_code}
                                onChange={(e) => handleDetailChange('postal_code', e.target.value)}
                                placeholder={language === 'el' ? 'π.χ. 10431' : 'e.g. 10431'}
                                data-testid="billing-postal-code"
                            />
                        </div>

                        {/* Country */}
                        <div className="space-y-2">
                            <Label htmlFor="country">
                                {language === 'el' ? 'Χώρα' : 'Country'}
                            </Label>
                            <Input
                                id="country"
                                value={billingDetails.country}
                                onChange={(e) => handleDetailChange('country', e.target.value)}
                                placeholder={language === 'el' ? 'π.χ. Ελλάδα' : 'e.g. Greece'}
                                data-testid="billing-country"
                            />
                        </div>

                        {/* Phone */}
                        <div className="space-y-2">
                            <Label htmlFor="phone" className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-slate-500" />
                                {language === 'el' ? 'Τηλέφωνο' : 'Phone'}
                            </Label>
                            <Input
                                id="phone"
                                value={billingDetails.phone}
                                onChange={(e) => handleDetailChange('phone', e.target.value)}
                                placeholder={language === 'el' ? 'π.χ. 210 1234567' : 'e.g. +30 210 1234567'}
                                data-testid="billing-phone"
                            />
                        </div>

                        {/* Email */}
                        <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="billing_email" className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-slate-500" />
                                {language === 'el' ? 'Email Τιμολόγησης' : 'Billing Email'}
                            </Label>
                            <Input
                                id="billing_email"
                                type="email"
                                value={billingDetails.email}
                                onChange={(e) => handleDetailChange('email', e.target.value)}
                                placeholder={language === 'el' ? 'π.χ. billing@company.gr' : 'e.g. billing@company.com'}
                                data-testid="billing-email"
                            />
                        </div>
                    </div>

                    {/* Save Button */}
                    <div className="mt-6 flex justify-end">
                        <Button
                            onClick={saveBillingDetails}
                            disabled={savingDetails}
                            className="bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white"
                            data-testid="save-billing-details"
                        >
                            {savingDetails ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            {language === 'el' ? 'Αποθήκευση Στοιχείων' : 'Save Details'}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
